import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';
import {
  resolveBugOriginSeries,
  type BugOriginBarSegment,
} from '../../../../shared/bugOriginReporter';
import type { DynamicsDiagnostics } from '../qualityApi';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

function BugOriginLegendStatic({ series }: { series: BugOriginBarSegment[] }) {
  return (
    <div className="dynamics-bug-origin-legend-custom">
      {series.map((s) => (
        <span key={s.key} className="dynamics-bug-origin-legend-entry">
          <span
            className="dynamics-bug-origin-legend-swatch"
            style={{ backgroundColor: s.color }}
            aria-hidden
          />
          <span>{s.label}</span>
        </span>
      ))}
    </div>
  );
}

function BugOriginChartBody({
  points,
  onPointClick,
  diagnostics,
}: {
  points: Array<Record<string, number | string | null>>;
  onPointClick?: (bucketLabel: string) => void;
  diagnostics?: DynamicsDiagnostics | null;
}) {
  const plotH = useDynamicsPlotHeight(300);
  const series = resolveBugOriginSeries(diagnostics);
  const rosterDriven = Boolean(diagnostics?.bugOriginSeries?.length);
  return (
    <div>
      <ResponsiveContainer width="100%" height={plotH}>
        <BarChart
          data={points}
          margin={{ ...dynamicsBucketChartMargins }}
          style={onPointClick ? { cursor: 'pointer' } : undefined}
          onClick={(e) => {
            const label = (e as { activeLabel?: string } | null | undefined)?.activeLabel;
            if (onPointClick && typeof label === 'string') onPointClick(label);
          }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
          <XAxis {...dynamicsPeriodAxisProps} />
          <YAxis />
          <Tooltip {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
          <Legend
            verticalAlign="bottom"
            content={<BugOriginLegendStatic series={series} />}
            wrapperStyle={{ paddingTop: 6 }}
          />
          {series.map((s) => (
            <Bar key={s.key} dataKey={s.key} stackId="1" name={s.label} fill={s.color} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <p
        className="dynamics-card__confidence"
        style={{
          marginTop: 6,
          fontSize: 11,
          color: 'var(--color-text-secondary, #888)',
          lineHeight: 1.45,
        }}
      >
        {rosterDriven ? (
          <>
            Segments follow Team roster roles (Profile → Manage roles). Reporters are matched to a
            roster row by display name or Jira username. Anyone not on the roster is grouped using
            the legacy name-pattern fallback (then Dev when still unmatched).
          </>
        ) : (
          <>
            No team roster for this product — stacks use reporter name patterns (QA / Dev /
            Support). Add members under Profile to align stacks with roles such as QA, Dev, Design,
            and Manager.
          </>
        )}
      </p>
    </div>
  );
}

export const DynamicsJ_BugOriginSplit: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="J"
      title="Bug origin split"
      hint="Who reported each bug, stacked by Team roster role when members exist; otherwise QA / Dev / Support name patterns."
      needs="Bug issues with a populated reporter. Classification uses Profile → Team roster (name / Jira id) when available."
      metric="bug-origin"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={300}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.J.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.J.bullets]}
    >
      {(points, onPointClick, diag) => (
        <BugOriginChartBody points={points} onPointClick={onPointClick} diagnostics={diag} />
      )}
    </DynamicsChartFrame>
  );
};
